import { useState, createContext, useEffect } from 'react';
import axios from 'axios';
import { resizeImageBatch } from '../../../utils/imageUtils';

export const ProjectsContext = createContext();
export const ProjectsProvider = ({ children }) => {
    const [projects, setProjects] = useState([]);
    const [currentProject, setCurrentProject] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        imageFiles: [],
        featured: false,
        completionDate: '',
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const backendUrl =
        import.meta.env.MODE === 'development'
            ? `http://${import.meta.env.VITE_BACKEND_URL}`
            : `https://${import.meta.env.VITE_BACKEND_URL_PROD}`;

    useEffect(() => {
        const fetchProjects = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${backendUrl}/projects`);
                setProjects(response.data);
            } catch (error) {
                console.error('Error fetching projects:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load projects. Please try again.',
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [backendUrl]);

    const handleSubmit = async (formData, validateForm) => {
        if (!validateForm()) {
            console.log('validateForm failed');
            return;
        }

        setLoading(true);

        try {
            const projectFormData = new FormData();
            const projectDataCopy = { ...formData };

            // Process imageFiles - separate files from paths
            const imageFiles = formData.imageFiles || [];
            const fileObjects = imageFiles.filter(
                (item) => item instanceof File
            );
            const imagePaths = imageFiles.filter(
                (item) => typeof item === 'string'
            );

            // Resize image files before upload (if they are actual files)
            let resizedFileObjects = [];
            if (fileObjects.length > 0) {
                resizedFileObjects = await resizeImageBatch(
                    fileObjects,
                    1920,
                    1080,
                    0.85
                );
            }

            // Set paths in project data and remove imageFiles array
            projectDataCopy.images = imagePaths;
            delete projectDataCopy.imageFiles;

            // Add JSON data and file objects to form
            projectFormData.append(
                'project_data',
                JSON.stringify(projectDataCopy)
            );
            resizedFileObjects.forEach((file, index) => {
                projectFormData.append(`files[${index}]`, file);
            });

            // API endpoint and configuration
            const endpoint = currentProject
                ? `${backendUrl}/projects/${currentProject._id}`
                : `${backendUrl}/projects`;
            const method = currentProject ? 'put' : 'post';

            // Get the token from localStorage
            const token = localStorage.getItem('token');

            const response = await axios[method](endpoint, projectFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: token ? `Bearer ${token}` : undefined,
                },
            });

            // Update state based on response
            if (currentProject) {
                setProjects(
                    projects.map((project) =>
                        project._id === currentProject._id
                            ? response.data
                            : project
                    )
                );
            } else {
                setProjects([...projects, response.data]);
            }

            setSnackbar({
                open: true,
                message: `Project ${
                    currentProject ? 'updated' : 'created'
                } successfully!`,
                severity: 'success',
            });
            setDialogOpen(false);
        } catch (error) {
            console.error('Error saving project:', error);
            setSnackbar({
                open: true,
                message: `Failed to save project: ${error.message}`,
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        console.log('handleChange', name, value, type);

        if (name === 'imageFiles') {
            // For multiple file inputs
            setFormData((prevFormData) => ({
                ...prevFormData,
                [name]: value, // This is an array of File objects
            }));
        } else {
            setFormData((prevFormData) => ({
                ...prevFormData,
                [name]: type === 'checkbox' ? checked : value,
            }));
        }

        // Clear error when user types
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: '',
            });
        }
    };

    const handleOpenDialog = (project = null) => {
        if (project) {
            setCurrentProject(project);
            setFormData({
                title: project.title,
                description: project.description,
                category: project.category,
                imageFiles: project.images || [],
                featured: project.featured,
                completionDate: project.completionDate,
            });
        } else {
            setCurrentProject(null);
            setFormData({
                title: '',
                description: '',
                category: '',
                imageFiles: [],
                featured: false,
                completionDate: '',
            });
        }
        setErrors({});
        setDialogOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({
            ...snackbar,
            open: false,
        });
    };

    const handleOpenDeleteDialog = (project) => {
        setCurrentProject(project);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!currentProject) return;

        setLoading(true);

        try {
            // Get the token from localStorage
            const token = localStorage.getItem('token');
            await axios.delete(`${backendUrl}/projects/${currentProject._id}`, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined,
                },
            });

            // For now, delete locally
            const filteredProjects = projects.filter(
                (project) => project._id !== currentProject._id
            );
            setProjects(filteredProjects);

            setSnackbar({
                open: true,
                message: 'Project deleted successfully!',
                severity: 'success',
            });

            setDeleteDialogOpen(false);
        } catch (error) {
            console.error('Error deleting project:', error);
            setSnackbar({
                open: true,
                message: 'Failed to delete project. Please try again.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <ProjectsContext.Provider
            value={{
                projects,
                setProjects,
                currentProject,
                setCurrentProject,
                loading,
                errors,
                setErrors,
                handleSubmit,
                handleDelete,
                snackbar,
                setSnackbar,
                dialogOpen,
                setDialogOpen,
                handleCloseSnackbar,
                backendUrl,
                deleteDialogOpen,
                setDeleteDialogOpen,
                handleOpenDeleteDialog,
                formData,
                handleChange,
                handleOpenDialog,
            }}
        >
            {children}
        </ProjectsContext.Provider>
    );
};
